/**
 * CorporateDocuments (C-14) — the non-GMP / corporate document register.
 * Category cards (HR / Finance / Procurement / Legal / IT / EHS / Facilities /
 * General) with live counts + a filterable list of GENERAL_CONTROLLED docs.
 * Route: /documents/corporate
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import { apiFetch } from '../../api/client';

interface CategoryStat { code: string; label: string; count: number }
interface CorporateDoc { documentId: string; docNumber: string | null; title: string; status: string; category: string | null; department: string | null; updatedAt: string }
interface CategoriesResp { categories: CategoryStat[]; items: CorporateDoc[]; total: number; totalGeneral: number }

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}

export function CorporateDocuments() {
  const navigate = useNavigate();
  const [category, setCategory] = useState('');

  const q = useQuery<CategoriesResp>({
    queryKey: ['document-categories', category],
    queryFn: ({ signal }) => apiFetch<CategoriesResp>(`/api/v1/document-categories${category ? `?category=${encodeURIComponent(category)}` : ''}`, { signal }),
    staleTime: 30_000,
  });

  const cats = q.data?.categories ?? [];
  const items = q.data?.items ?? [];
  const labelOf = (code: string | null) => cats.find((c) => c.code === code)?.label ?? code ?? '—';

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Documents"
        title="Corporate Documents"
        subtitle="Non-GMP, general-controlled documents organised by corporate function — HR, Finance, Procurement, Legal, IT, EHS and more. Controlled and versioned, on a lighter (non-GMP) review path."
      />

      <div className={styles.kpiRow}>
        <button type="button" className={`${styles.kpi} ${category === '' ? styles.kpiAlert : ''}`} onClick={() => setCategory('')}>
          <span className={styles.kpiNum}>{q.data?.totalGeneral ?? 0}</span><span className={styles.kpiLabel}>All corporate</span>
        </button>
        {cats.map((c) => (
          <button key={c.code} type="button" className={`${styles.kpi} ${category === c.code ? styles.kpiAlert : ''}`} onClick={() => setCategory(c.code)}>
            <span className={styles.kpiNum}>{c.count}</span><span className={styles.kpiLabel}>{c.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.filters}>
        <select aria-label="Category filter" value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select}>
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} document{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading corporate documents…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No corporate documents{category ? ' in this category' : ''} yet. Create one with the “General-controlled” profile.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>Document</th><th>Title</th><th>Category</th><th>Status</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.documentId} className={styles.row} onClick={() => navigate({ to: '/documents/$id', params: { id: d.documentId } })}>
                <td className={styles.mono}>{d.docNumber ?? '—'}</td>
                <td>{d.title}</td>
                <td>{d.category ? <span className={styles.pill}>{labelOf(d.category)}</span> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                <td><span className={styles.statusPill}>{d.status}</span></td>
                <td>{fmtDate(d.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
